import React, { useMemo, useState } from 'react';
import { Maximize2 } from 'lucide-react';
import type { HourForecast } from '../../../../shared/types';
import { TimeService, addDays, mondayOf } from '../../../services/timeService';
import {
  WidgetFrame,
  WidgetActionButton,
  Stat,
  WChart,
  WList,
  type WListRow,
} from '../../../components/widgets';
import { pageGate } from '../../../lib/pageGates';
import { fmtHours, todayIso, shortWeek, useSettledFetch } from './shared';
import type { ProjectWidgetCtx, ProjectWidgetDef } from './context';

// ═══════════════════════════════════════════════════════════════
// FORECAST — NEXT 4 WEEKS (Widgets R2 T2b; function, drills, takeover,
// and requiresAny unchanged from forecast_outlook). The private recharts
// bar chart is replaced by the shared `WChart` now that it carries an
// `onBarClick` hook (T1), so the per-week -> forecast-week drill survives
// on the shared form.
// ═══════════════════════════════════════════════════════════════

function ForecastOutlookBody({ ctx }: { ctx: ProjectWidgetCtx }) {
  const [forecasts, setForecasts] = useState<HourForecast[]>([]);

  const start = useMemo(() => addDays(mondayOf(todayIso()), 7), []);
  const end = useMemo(() => addDays(start, 28), [start]);

  const { status, reload } = useSettledFetch(
    () => [TimeService.listForecastsForRange(start, end)],
    (results) => {
      const [fR] = results;
      if (fR.status === 'fulfilled')
        setForecasts(fR.value.filter((f: HourForecast) => f.projectId === ctx.project.id));
    },
    [ctx.project.id, start, end]
  );

  const weekData = useMemo(() => {
    const buckets: Record<string, number> = {};
    for (let w = 0; w < 4; w++) buckets[addDays(start, w * 7)] = 0;
    for (const f of forecasts) {
      const ws = mondayOf(f.dayDate || f.weekStartDate);
      if (ws in buckets) buckets[ws] += Number(f.hours) || 0;
    }
    return Object.entries(buckets).map(([weekStart, hours]) => ({
      label: shortWeek(weekStart),
      weekStart,
      hours,
    }));
  }, [forecasts, start]);

  const totalForecastHours = weekData.reduce((s, d) => s + d.hours, 0);

  const memberRollup = useMemo(() => {
    const byMember = new Map<string, number>();
    for (const f of forecasts) {
      byMember.set(f.teamMemberId, (byMember.get(f.teamMemberId) || 0) + (Number(f.hours) || 0));
    }
    return Array.from(byMember.entries())
      .map(([id, hours]) => ({
        id,
        name: ctx.members.find((m) => m.id === id)?.name || 'Unknown',
        hours,
      }))
      .sort((a, b) => b.hours - a.hours);
  }, [forecasts, ctx.members]);

  const frameStatus = status === 'loading' ? 'loading' : status === 'error' ? 'error' : 'ready';
  // Project Canvas R1 (T3b, Decision 5/6): expand nests the real
  // Forecasting page via the project host instead of the widget's own
  // takeover — one path, same gate the sidebar Forecasting nav item uses
  // (no expand control at all when the viewer lacks it; never an expand
  // that opens to a refusal).
  const canOpenForecasting = pageGate('forecasting', ctx.userPermissions);

  const memberRows: WListRow[] = memberRollup.map((m) => ({
    id: m.id,
    primary: m.name,
    trailing: (
      <span
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--tm)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        {fmtHours(m.hours)}
      </span>
    ),
  }));

  return (
    <WidgetFrame
      title="Forecast"
      status={frameStatus}
      form="chart"
      error={{ message: 'Could not load forecast data.', onRetry: reload }}
      actions={
        canOpenForecasting ? (
          <WidgetActionButton
            icon={Maximize2}
            label="Open forecast"
            onClick={() => ctx.navigate?.({ view: 'forecasting', projectId: ctx.project.id })}
          />
        ) : undefined
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <Stat
          value={fmtHours(totalForecastHours)}
          label="forecasted next 4 weeks"
          compact
          onClick={() => ctx.onDrill({ kind: 'forecast-week', weekStart: start })}
        />
        <WChart
          kind="bar"
          data={weekData}
          series={[{ key: 'hours', label: 'Forecast', tone: 'forecast' }]}
          xKey="label"
          height={160}
          onBarClick={(_key, row) =>
            ctx.onDrill({ kind: 'forecast-week', weekStart: row.weekStart })
          }
          tooltipFormatter={(v) => [`${v}h`, 'Forecast']}
        />
        {memberRows.length > 0 && <WList rows={memberRows} max={8} />}
      </div>
    </WidgetFrame>
  );
}

export const forecastOutlookWidget: ProjectWidgetDef = {
  id: 'forecast_outlook',
  title: 'Forecast',
  description: 'Hours forecasted on this project for the upcoming 4 weeks. Per-person in expanded.',
  category: 'people',
  // Not `requires` — forecasting.view_own/_team/_all have no implication
  // map in hasPermission (exact-key match only), so a single-key `requires`
  // would wrongly hide this from a view_team/view_all-only holder.
  requiresAny: ['forecasting.view_own', 'forecasting.view_team', 'forecasting.view_all'],
  cols: { min: 3, max: 12 },
  defaultSize: 'standard',
  inputSurface: false,
  render: (ctx) => <ForecastOutlookBody ctx={ctx} />,
  // Project Canvas R1 (T3b): expand now routes through `ctx.navigate` →
  // `ProjectToolHost` (same nested real page), so this widget no longer
  // needs its own `takeover` — one path (Finding 3), not two.
};
